'use client'

import { detectDeviceClass, monitorPerformance } from '@/lib/performance'
import { useFeatureFlagEnabled } from 'posthog-js/react'
import { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import {
  loadSettings,
  setPerformanceMode,
} from '@/lib/features/animatedBackground/animatedBackgroundSlice'
import type { RootState } from '@/lib/store'
import { AnimatedBackgroundCanvas } from './AnimatedBackgroundCanvas'
import { AnimatedBackgroundWebGL } from './AnimatedBackgroundWebGL'

interface AnimatedBackgroundProps {
  disabled?: boolean
  debugMode?: boolean
  forceVersion?: 'webgl' | 'canvas'
}

const checkWebGLSupport = (): boolean => {
  if (typeof window === 'undefined') return false

  try {
    const canvas = document.createElement('canvas')
    const gl =
      canvas.getContext('webgl') ||
      (canvas.getContext('experimental-webgl') as WebGLRenderingContext | null)

    if (!gl) return false

    const renderer = gl.getParameter(gl.RENDERER) || ''
    const vendor = gl.getParameter(gl.VENDOR) || ''

    const problematicPatterns = [/swiftshader/i, /llvmpipe/i, /microsoft basic render driver/i]

    const isProblematic = problematicPatterns.some(
      (pattern) => pattern.test(renderer) || pattern.test(vendor)
    )

    return !isProblematic
  } catch {
    return false
  }
}

export function AnimatedBackground({
  disabled = false,
  debugMode = false,
  forceVersion,
}: AnimatedBackgroundProps) {
  const dispatch = useDispatch()

  // Check PostHog feature flag
  const animatedBackgroundEnabled = useFeatureFlagEnabled('animated_background')

  const animatedBackgroundState = useSelector((state: RootState) => {
    return state.animatedBackground || null
  })

  const { isEnabled, performanceMode } = animatedBackgroundState || {}

  const [webglSupported, setWebglSupported] = useState<boolean | null>(null)
  const [modeChangeCount, setModeChangeCount] = useState(0)
  useEffect(() => {
    setWebglSupported(checkWebGLSupport())
  }, [])

  useEffect(() => {
    dispatch(loadSettings())
  }, [dispatch])

  const performanceCheck = useCallback(async () => {
    if (!isEnabled) return

    try {
      const metrics = await monitorPerformance(3000)
      const deviceClass = detectDeviceClass()

      let suggestedMode = performanceMode

      if (deviceClass === 'low') {
        suggestedMode = performanceMode === 'high' ? 'medium' : performanceMode
      } else if (deviceClass === 'high' && performanceMode === 'low') {
        suggestedMode = 'medium'
      }

      // Require more extreme conditions to change modes
      const veryUnstableFPS = metrics.stability < 0.6
      const veryLowFPS = metrics.fps < 20
      const veryGoodFPS = metrics.fps > 50 && metrics.stability > 0.85

      if (veryLowFPS || veryUnstableFPS) {
        if (performanceMode === 'high') suggestedMode = 'medium'
        else if (performanceMode === 'medium') suggestedMode = 'low'
      } else if (veryGoodFPS && performanceMode === 'low') {
        suggestedMode = 'medium'
      } else if (metrics.fps > 58 && metrics.stability > 0.92 && performanceMode === 'medium') {
        suggestedMode = 'high'
      }

      if (suggestedMode !== performanceMode) {
        dispatch(setPerformanceMode(suggestedMode))
        setModeChangeCount((prev) => prev + 1)
      }
    } catch (error) {
      console.error('Error in performance check:', error)
      const deviceClass = detectDeviceClass()
      if (performanceMode !== deviceClass) {
        dispatch(setPerformanceMode(deviceClass))
        setModeChangeCount((prev) => prev + 1)
      }
    }
  }, [isEnabled, performanceMode, dispatch, modeChangeCount])

  useEffect(() => {
    if (!isEnabled) return

    // Wait longer before first check to allow animation to stabilize
    const initialTimeout = setTimeout(performanceCheck, 5000)

    // Check less frequently to reduce mode switching
    const interval = setInterval(performanceCheck, 30000)

    return () => {
      clearTimeout(initialTimeout)
      clearInterval(interval)
    }
  }, [performanceCheck, isEnabled])

  useEffect(() => {
    if (typeof window === 'undefined') return

    // Only set initial performance mode if not already set in localStorage
    if (performanceMode !== 'high' && performanceMode !== 'medium' && performanceMode !== 'low') {
      const isMobile = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      )

      const isLowEndDevice = () => {
        if ('deviceMemory' in navigator) {
          return (navigator as any).deviceMemory <= 4
        }

        const canvas = document.createElement('canvas')
        const gl =
          canvas.getContext('webgl') ||
          (canvas.getContext('experimental-webgl') as WebGLRenderingContext | null)
        if (gl) {
          const renderer = gl.getParameter(gl.RENDERER)
          if (renderer && typeof renderer === 'string') {
            const lowEndGPUs = ['PowerVR', 'Adreno 3', 'Adreno 4', 'Mali-4', 'VideoCore']
            if (lowEndGPUs.some((gpu) => renderer.includes(gpu))) {
              return true
            }
          }
        }

        return false
      }

      if (isMobile && isLowEndDevice()) {
        dispatch(setPerformanceMode('low'))
      } else if (isMobile) {
        dispatch(setPerformanceMode('medium'))
      }
    }
  }, [dispatch, performanceMode])

  // Return null if feature flag is disabled (undefined means loading, false means disabled)
  if (!animatedBackgroundEnabled) {
    return null
  }

  if (!animatedBackgroundState || disabled || !isEnabled) {
    return null
  }

  if (webglSupported === null) {
    return null
  }

  const renderComponent = () => {
    if (forceVersion === 'webgl' || (forceVersion !== 'canvas' && webglSupported)) {
      return <AnimatedBackgroundWebGL debugMode={debugMode} />
    } else {
      return <AnimatedBackgroundCanvas debugMode={debugMode} />
    }
  }

  return <div className="animated-background">{renderComponent()}</div>
}
